/**
 * Builds relative paths for DAR deep-links from Ask Vantage (`data-vantage-link`).
 * Prefer a single manifest **path** (kebab-case segments); legacy separate slug keys still work.
 *
 * Date/time in the **resulting URL** must match the rest of Vantage (see `buildFilterQueryParams`):
 * query keys `from` and `to`, values `YYYY-MM-DD.HHMM` — time is four digits after a dot on the same
 * string, e.g. `sales/orders-and-sales/report?from=2026-04-12.0600&to=2026-04-13.0600`.
 *
 * In `data-params`, pass `date_from` / `date_to` as `YYYY-MM-DD` and optional `from_time` / `to_time`
 * as `HH:MM`; this module maps them to that `from`/`to` format.
 */
import { buildFilterQueryParams } from '../../../helpers/utils';

const ROUTE_PATTERNS: Record<string, string> = {
  extended_analysis: '/:dashboard_name/:dashboard_widget_name',
  report: '/:dashboard_name/:dashboard_widget_name/report'
};

export type ResolvedDarSegments = {
  dashboard_name: string;
  dashboard_widget_name: string;
  analytics_widget_name?: string;
};

/** Parses optional HH:MM; returns null if missing/invalid */
function parseHm(s?: string): [number, number] | null {
  if (!s?.trim()) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return [h, min];
}

/**
 * Time-of-day range for the selected date range (start date's start time, end date's end time).
 * If only dates are given, uses full calendar days [[0,0],[23,59]].
 */
export function parseTimeRangeFromAskParams(
  params: Record<string, string>
): [[number, number], [number, number]] | undefined {
  const st = parseHm(params.from_time);
  const et = parseHm(params.to_time);
  if (st && et) return [st, et];
  if (params.date_from && params.date_to) return [[0, 0], [23, 59]];
  return undefined;
}

/** Date / time / location extracted from `data-params` for Extended A + report URLs */
export function deepLinkFiltersFromParams(params: Record<string, string>): {
  dateRange?: [string, string];
  timeRange?: [[number, number], [number, number]];
  locationIds?: number[];
} {
  const { date_from, date_to, location_id } = params;
  if (!date_from || !date_to) return {};
  const timeRange = parseTimeRangeFromAskParams(params);
  const out: {
    dateRange: [string, string];
    timeRange?: [[number, number], [number, number]];
    locationIds?: number[];
  } = { dateRange: [date_from, date_to] };
  if (timeRange) out.timeRange = timeRange;
  if (location_id?.trim()) {
    const id = parseInt(location_id, 10);
    if (!Number.isNaN(id)) out.locationIds = [id];
  }
  return out;
}

/**
 * Resolves `path` ("a/b/c" or "a/b") or legacy dashboard_* keys into route segments.
 */
export function resolveDarSegments(
  entity: string,
  params: Record<string, string>
): ResolvedDarSegments | null {
  const path = params.path?.trim();
  if (path) {
    const parts = path.split('/').filter(Boolean);
    if (entity === 'report' && parts.length === 2) {
      return { dashboard_name: parts[0], dashboard_widget_name: parts[1] };
    }
    if (entity === 'extended_analysis' && parts.length === 3) {
      return {
        dashboard_name: parts[0],
        dashboard_widget_name: parts[1],
        analytics_widget_name: parts[2]
      };
    }
    return null;
  }

  const { dashboard_name, dashboard_widget_name, analytics_widget_name } = params;
  if (!dashboard_name || !dashboard_widget_name) return null;

  if (entity === 'extended_analysis') {
    if (!analytics_widget_name) return null;
    return { dashboard_name, dashboard_widget_name, analytics_widget_name };
  }

  return { dashboard_name, dashboard_widget_name };
}

export function buildDarUrl(
  entity: string,
  params: Record<string, string>
): string | null {
  const pattern = ROUTE_PATTERNS[entity];
  if (!pattern) return null;

  const resolved = resolveDarSegments(entity, params);
  if (!resolved) return null;

  const enc = (s: string) => encodeURIComponent(s);

  let routePath = pattern
    .replace(':dashboard_name', enc(resolved.dashboard_name))
    .replace(':dashboard_widget_name', enc(resolved.dashboard_widget_name));

  const query: string[] = [];
  const dateFrom = params.date_from;
  const dateTo = params.date_to;
  if (dateFrom && dateTo) {
    const timeRange =
      parseTimeRangeFromAskParams(params) ??
      ([
        [0, 0],
        [23, 59]
      ] as [[number, number], [number, number]]);
    const qp = buildFilterQueryParams({
      dateRange: [dateFrom, dateTo],
      timeRange
    });
    query.push(`from=${enc(String(qp.from))}`);
    query.push(`to=${enc(String(qp.to))}`);
  }
  return query.length > 0 ? `${routePath}?${query.join('&')}` : routePath;
}
